import { Box, HStack, Heading, Switch, Text, useColorModeValue } from "@chakra-ui/react";
import { CellClassParams, ColDef, IRowNode } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";
import { NoPatientData } from "../no-data";
import { TableGrid } from "../table-grid";
import { PatientQueryParamProps } from "../../shared-logic/consolidated-context/reducer";

interface FilterToggle {
  label: string;
  filterFn: (rowData: IRowNode["data"]) => boolean;
}

export interface FhirSectionProps {
  sectionId: string;
  sectionName: string;
  rowData: object[];
  defaultColDef?: ColDef;
  columnDefs: ColDef[];
  filterToggle?: FilterToggle;
  denseDisplay?: boolean;
  patientQueryParams?: PatientQueryParamProps | undefined;
  usingSectionSearch?: boolean;
  usingMetriportOnlyBundleFromConsolidated?: boolean;
  handleCellClick: (sectionId: string, event: { data: object }) => void;
  handleCellStyle: (params: CellClassParams) => { backgroundColor: string } | undefined;
}

function FhirSection({
  sectionId, // same as key
  sectionName,
  rowData,
  defaultColDef,
  columnDefs,
  patientQueryParams,
  filterToggle,
  handleCellClick,
  denseDisplay = false,
  usingSectionSearch = false,
  usingMetriportOnlyBundleFromConsolidated = false,
}: FhirSectionProps) {
  const [isFilterActive, setIsFilterActive] = useState(false);
  const gridRef = useRef<AgGridReact>(null);
  const isFilterActiveRef = useRef(isFilterActive);
  const hasFilterToggle = !!filterToggle;

  useEffect(() => {
    isFilterActiveRef.current = isFilterActive;
    gridRef.current?.api?.onFilterChanged();
  }, [isFilterActive]);

  useEffect(() => {
    const sectionKeyParam = patientQueryParams?.sectionKey;
    const resourceIdParam = patientQueryParams?.resourceId;
    if (sectionKeyParam && resourceIdParam && sectionKeyParam === sectionId) {
      const selectedRow = rowData.find((row: { id?: string }) => row.id === resourceIdParam);
      if (selectedRow) {
        handleCellClick(sectionId, { data: selectedRow });
      }
    }
  }, []);

  const toggleFilter = () => {
    setIsFilterActive(!isFilterActive);
  };

  const gridOptionsForToggle = {
    isExternalFilterPresent: () => hasFilterToggle,
    doesExternalFilterPass: (node: IRowNode) => {
      return !isFilterActiveRef.current || filterToggle?.filterFn(node.data) ? true : false;
    },
  };

  const toggleItem = filterToggle && (
    <Box
      ml={"auto"}
      ms={3}
      border="solid"
      borderRadius={"30px"}
      borderColor={useColorModeValue("gray.200", "gray.700")}
      justifySelf={"flex-end"}
    >
      <HStack ms={5} me={5} fontWeight={"medium"}>
        <Text m={2}>{filterToggle.label}</Text>
        <Switch
          isChecked={isFilterActive}
          onChange={toggleFilter}
          colorScheme={"blue"}
          size={"lg"}
        />
      </HStack>
    </Box>
  );

  return (
    <Box
      id={sectionId}
      w={"100%"}
      mb={5}
      borderRadius={10}
      bg={useColorModeValue("white", "gray.800")}
      px={7}
      py={5}
      border="solid"
      borderColor={useColorModeValue("gray.200", "gray.700")}
    >
      <HStack mb={5} w={"100%"} justifyContent={"space-between"}>
        <Heading size={denseDisplay ? "xs" : "md"}>{sectionName}</Heading>
        {toggleItem}
      </HStack>
      {rowData.length > 0 ? (
        <TableGrid
          ref={gridRef}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={rowData}
          usingSectionSearch={usingSectionSearch}
          gridOptions={gridOptionsForToggle}
        />
      ) : (
        <NoPatientData
          message={
            usingMetriportOnlyBundleFromConsolidated ? "EHR is in sync" : "No Data Available"
          }
        />
      )}
    </Box>
  );
}

export default FhirSection;
